/* =====================================================================
 * 어드민 대시보드 — 기간 프리셋과 그 기간에 맞는 집계 단위.
 *
 * 차트와 하단 표는 같은 프리셋(최근 7·30·90·365일·전체)을 쓴다. 차트는
 * 버킷이 있으므로 기간이 길어질수록 굵은 단위(일 → 주 → 월)로 접어야
 * 막대 수가 화면에 맞는데, 그 판정을 화면이 아니라 여기서 한다 — 프리셋과
 * 사용자 지정 날짜 범위가 같은 규칙을 타야 두 경로의 차트 모양이 다르지
 * 않다.
 * ===================================================================== */

import {
  ADMIN_DASHBOARD_MAX_RANGE_DAYS,
  type AdminDashboardGranularity,
} from '@auw/api-client';

/**
 * 기간 프리셋. 마지막 항목은 서버 보존기간 전체("전체").
 *
 * 차트와 표가 같은 목록을 쓴다 — 한쪽에만 있는 기간이 생기면 "표는 90일인데
 * 차트는 왜 못 고르나" 가 된다.
 */
export const ADMIN_DASHBOARD_RANGE_DAYS: readonly number[] = [
  7,
  30,
  90,
  365,
  ADMIN_DASHBOARD_MAX_RANGE_DAYS,
];

/** 기본 프리셋. 요약 타일의 30일과 같아서 타일과 차트가 같은 구간을 본다. */
export const ADMIN_DASHBOARD_DEFAULT_RANGE_DAYS = 30;

/**
 * 기간 길이에 맞는 집계 단위.
 *
 * 경계는 막대 수로 정했다: 일별은 31개까지(한 달), 주별은 120일까지(약 17개),
 * 그 위는 월별이다. 365일이면 12개, 보존기간 전체(1830일)면 최대 60개인데
 * 서버는 기록이 있는 버킷만 돌려주므로 실제로는 사용 이력이 있는 달만 나온다.
 * 연별은 쓰지 않는다 — 전체를 연 단위로 접으면 막대가 한두 개라 추이가
 * 아니라 합계가 된다.
 */
export function granularityForDays(days: number): AdminDashboardGranularity {
  if (days <= 31) return 'day';
  if (days <= 120) return 'week';
  return 'month';
}

/**
 * 두 ISO 날짜(YYYY-MM-DD) 사이의 일수, 양 끝 포함.
 *
 * UTC 로 계산한다 — 날짜 문자열은 시간대가 없는 달력 날짜이고, 로컬 시간대로
 * 파싱하면 서머타임 경계에서 하루가 23·25시간이 되어 나눗셈이 어긋난다.
 * 유효하지 않은 입력이면 0 (호출자는 이미 검증을 거친 값을 넘긴다).
 */
export function inclusiveDaysBetween(start: string, end: string): number {
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return 0;
  }
  return Math.floor((endMs - startMs) / 86_400_000) + 1;
}

/**
 * 화면이 실제로 보고 있는 구간의 집계 단위.
 *
 * 사용자 지정 범위가 적용돼 있으면 그 길이를, 아니면 프리셋 일수를 쓴다.
 */
export function granularityForWindow(
  range: { start: string; end: string } | null,
  presetDays: number,
): AdminDashboardGranularity {
  return granularityForDays(
    range ? inclusiveDaysBetween(range.start, range.end) : presetDays,
  );
}
